import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const ROLES = ['annotator', 'user', 'superuser'];

const UsersAdminPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ username: '', password: '', role: 'user' });
  const [busy, setBusy] = useState(false);
  const [pendingRoles, setPendingRoles] = useState({});
  const [pendingActive, setPendingActive] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get('/api/users');
      setUsers(r.data.users || []);
      setPendingRoles({});
      setPendingActive({});
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await axios.post('/api/users', form);
      setForm({ username: '', password: '', role: 'user' });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  const handleRoleSelect = (id, role) => {
    setPendingRoles((prev) => {
      const next = { ...prev };
      const u = users.find((x) => x.id === id);
      if (u && u.role === role) {
        delete next[id];
      } else {
        next[id] = role;
      }
      return next;
    });
  };

  const handleActiveToggle = (u) => {
    setPendingActive((prev) => {
      const next = { ...prev };
      const currentEffective = next[u.id] !== undefined ? next[u.id] : u.is_active;
      const newVal = !currentEffective;
      if (newVal === u.is_active) {
        delete next[u.id];
      } else {
        next[u.id] = newVal;
      }
      return next;
    });
  };

  const dirtyIds = useMemo(() => {
    const ids = new Set();
    Object.keys(pendingRoles).forEach((id) => ids.add(Number(id)));
    Object.keys(pendingActive).forEach((id) => ids.add(Number(id)));
    return ids;
  }, [pendingRoles, pendingActive]);

  const handleSaveAll = async () => {
    if (dirtyIds.size === 0) return;
    setSavingAll(true);
    setError('');
    try {
      const updates = Array.from(dirtyIds).map((id) => {
        const payload = {};
        if (pendingRoles[id] !== undefined) payload.role = pendingRoles[id];
        if (pendingActive[id] !== undefined) payload.is_active = pendingActive[id];
        return axios.patch(`/api/users/${id}`, payload);
      });
      await Promise.all(updates);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save changes');
    } finally {
      setSavingAll(false);
    }
  };

  const handleDiscardAll = () => {
    setPendingRoles({});
    setPendingActive({});
  };

  const handleResetPassword = async (id) => {
    const pw = window.prompt('Enter new password:');
    if (!pw) return;
    try {
      await axios.patch(`/api/users/${id}`, { password: pw });
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`/api/users/${id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to delete user');
    }
  };

  const dirtyCount = dirtyIds.size;

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-4 sm:p-6">
      <h1 className="text-xl font-bold text-blue-600 mb-4">User management</h1>

      <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-6">
        <input
          className="border rounded px-2 py-1"
          placeholder="username"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          required
        />
        <input
          className="border rounded px-2 py-1"
          type="password"
          placeholder="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
        />
        <select
          className="border rounded px-2 py-1"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          type="submit"
          disabled={busy}
          className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 disabled:opacity-50"
        >
          Create user
        </button>
      </form>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-slate-500">
              {dirtyCount > 0
                ? `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}`
                : 'No unsaved changes'}
            </div>
            <div className="space-x-2">
              <button
                onClick={handleDiscardAll}
                disabled={dirtyCount === 0 || savingAll}
                className="border rounded px-3 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Discard
              </button>
              <button
                onClick={handleSaveAll}
                disabled={dirtyCount === 0 || savingAll}
                className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingAll ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b">
              <tr>
                <th className="py-2">ID</th>
                <th>Username</th>
                <th>Role</th>
                <th>Active</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const effectiveRole = pendingRoles[u.id] ?? u.role;
                const effectiveActive = pendingActive[u.id] !== undefined ? pendingActive[u.id] : u.is_active;
                const rowDirty = dirtyIds.has(u.id);
                return (
                  <tr key={u.id} className={`border-b last:border-b-0 ${rowDirty ? 'bg-yellow-50' : ''}`}>
                    <td className="py-2">{u.id}</td>
                    <td>{u.username}</td>
                    <td>
                      <select
                        value={effectiveRole}
                        onChange={(e) => handleRoleSelect(u.id, e.target.value)}
                        className="border rounded px-1 py-0.5"
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td>
                      <button
                        onClick={() => handleActiveToggle(u)}
                        className={`px-2 py-0.5 rounded ${effectiveActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}
                      >
                        {effectiveActive ? 'active' : 'disabled'}
                      </button>
                    </td>
                    <td className="text-slate-500">{u.created_at?.replace('T', ' ').slice(0, 19)}</td>
                    <td className="space-x-2 text-right">
                      <button onClick={() => handleResetPassword(u.id)} className="text-blue-600 hover:underline">reset pw</button>
                      <button onClick={() => handleDelete(u.id)} className="text-red-600 hover:underline">delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default UsersAdminPage;
